// ---------------------------------------------------------------------------
// The dispatch receipt's data, shaped from the order's frozen snapshot.
//
// A delivery dispatched from the till prints a NORMAL customer receipt the
// moment it leaves - the driver takes it with the goods - but no sale exists
// yet and none is fabricated to render one. Instead the order's own frozen
// figures are laid over the Sale shape `ReceiptSheet` reads:
//
//   * every line, price and total is the order's exact lira snapshot - the
//     same figures the later account collection will settle to the pound;
//   * the paper is identified by the RECEIPT number (YB-…) reserved at
//     dispatch - the same identity the settled sale will keep for ever - with
//     the ORDER number printed beside it as the operational reference;
//   * payments are simply EMPTY. Nothing is printed for them - no fake cash
//     row, and no "unpaid" wording either: payment pending is internal state,
//     and the customer's paper looks like any other receipt.
//
// Money is carried, never computed: this file moves strings from one shape to
// another and does no arithmetic at all.
// ---------------------------------------------------------------------------

import { toDecimal } from './format';
import type { CustomerOrder, Sale } from '../types';

export function dispatchReceiptSale(order: CustomerOrder): Sale {
  return {
    id: order.id,
    // The receipt identity is the YB number reserved when the delivery was
    // created - byte-identical to what the completed sale will carry. ONLY a
    // legacy order dispatched before reservation existed has none; its paper
    // was printed with the OR number, so its preview honestly shows the same.
    receiptNumber: order.receiptNumber ?? order.orderNumber,
    // The operational reference, beside the identity - never as it.
    orderNumber: order.orderNumber,
    // The online customer, frozen on the order, for the receipt.
    onlineOrder: order.onlineOrder ?? null,
    // The design stamped at dispatch: the preview and any manual copy render
    // the layout the driver's paper was printed under, not today's.
    receiptDesignVersionId: order.receiptDesignVersionId,
    status: 'COMPLETED',
    subtotalUsd: subtotalUsdOf(order),
    discountUsd: '0.00',
    discountReason: null,
    deliveryFeeUsd: order.deliveryFeeUsd,
    fulfilment: 'DELIVERY',
    tableLabel: null,
    preparationDispatch: order.preparationDispatch,
    totalUsd: order.estimatedTotalUsd,
    voidedAt: null,
    voidReason: null,
    voidedBy: null,
    totalLbp: order.totalLbp ?? '0',
    subtotalLbp: order.subtotalLbp,
    discountLbp: '0',
    deliveryFeeLbp: order.deliveryFeeLbp,
    exchangeRateUsdToLbp: order.exchangeRateUsdToLbp ?? '0',
    changeCurrency: 'USD',
    changeAmount: '0',
    changeUsd: '0',
    itemCount: order.items.length,
    note: order.note,
    createdAt: order.createdAt,
    cashier: { ...order.createdBy, username: '' },
    // An order belongs to no shift - dispatch is not a financial event. The
    // empty string renders as an empty value in the one layout that shows a
    // shift number, rather than borrowing a shift the order is not part of.
    shift: { id: '', shiftNumber: '' },
    items: order.items.map((item) => ({
      id: item.id,
      productId: item.product.id,
      nameEn: item.product.nameEn,
      nameAr: item.product.nameAr,
      variantId: item.variantId,
      variantNameEn: item.variantNameEn,
      variantNameAr: item.variantNameAr,
      variantLabelEn: item.variantLabelEn,
      variantLabelAr: item.variantLabelAr,
      unitPriceLbp: item.unitPriceLbp ?? null,
      lineTotalLbp: item.lineTotalLbp ?? null,
      // The promotion frozen on the order line, carried as-is.
      basePriceLbp: item.basePriceLbp ?? null,
      promotionType: item.promotionType ?? 'NONE',
      discountPercent: item.discountPercent ?? null,
      discountAmountLbp: item.discountAmountLbp ?? null,
      comboOfferId: item.comboOfferId ?? null,
      comboGroupKey: item.comboGroupKey ?? null,
      comboNameEn: item.comboNameEn ?? null,
      comboNameAr: item.comboNameAr ?? null,
      comboOfferPriceLbp: item.comboOfferPriceLbp ?? null,
      comboRegularPriceLbp: item.comboRegularPriceLbp ?? null,
      sku: item.product.sku,
      sellingUnit: item.product.sellingUnit,
      // The mode is display semantics; the frozen quantity is the fact. A
      // money-amount line was already resolved to a weight at dispatch,
      // exactly as the collection will replay it.
      quantityMode:
        item.product.sellingUnit === 'KG' || !toDecimal(item.quantity).isInteger()
          ? 'WEIGHT_KG'
          : 'COUNT',
      quantity: item.quantity,
      unitPrice: item.unitPriceUsd,
      lineTotalUsd: item.lineTotalUsd,
      note: item.preparationNotes,
      refundedQuantity: '0',
    })),
    payments: [],
  };
}

/// The order's item subtotal in dollars: total minus fee, both frozen. The
/// one subtraction in this file, done on the frozen figures the server wrote
/// - never a re-pricing.
function subtotalUsdOf(order: CustomerOrder): string {
  return toDecimal(order.estimatedTotalUsd).minus(toDecimal(order.deliveryFeeUsd)).toFixed(2);
}
